"use client";

import React, { useMemo } from "react";

import dayjs from "dayjs";

import useSelectedStores from "@lib/hooks/use-selected-stores";
import { useKpisOverview } from "@lib/api-hooks";
import {
  useAttributionPresetState,
  useDateRangeState,
} from "@lib/hooks/url-state";
import {
  EDUCATION_BANNER,
  START_DATE_RANGE_SELECT_OPTIONS_FULL,
} from "@/constants/constants";
import { useInitialTimeRange } from "@lib/hooks/use-initial-timerange";
import CollapsibleBanner from "@components/CollapsibleBanner/CollapsibleBanner";
import LearnMoreInfo from "@components/LearnMoreInfo/LearnMoreInfo";
import { Header } from "../../Header/Header";
import { MarketingPerformance } from "./MarketingPerformance";
import { StorePerformanceContainer } from "./StorePerformanceContainer";
import { useKpisPerChannel } from "@lib/api-hooks/useKpisPerChannel";
import MarketingChannelOverviewTable from "./MarketingChannelOverviewTable";
import { DiscountCodeRedemptions } from "./DiscountCodeRedemptions";
import { PpsSurveyChannelOverviewChart } from "./PpsSurveyChannelOverviewChart";
import {
  checkHasOverallData,
  Granularity,
} from "@lib/api-hooks/useKpiBreakdownForChannel";
import axios from "axios";
import { gatewayTimeoutMessage } from "@lib/api-hooks/utils/error-handling";
import { useGranularity } from "@lib/hooks/use-granularity";
import { useReportingDateLimits } from "@lib/hooks/use-most-ahead-timezone";
import { useFeatureFlag } from "@lib/hooks/useFeatureFlags";

export function MarketingOverviewDashboard() {
  const { isPending } = useSelectedStores();
  const isDecifyPpsEnabled = useFeatureFlag("decify_pps");
  const { maxDate, timezone } = useReportingDateLimits();

  const initialTimeRange = useMemo(() => {
    const now = timezone ? dayjs().tz(timezone) : dayjs();
    return {
      startDate: now.subtract(7, "days").startOf("day").toDate(),
      endDate: now.subtract(1, "days").endOf("day").toDate(),
      timerange:
        "last_7_days" as (typeof START_DATE_RANGE_SELECT_OPTIONS_FULL)[number]["value"],
    };
  }, [timezone]);

  const { initialTimeRangeIsSet } = useInitialTimeRange({
    maxDate,
    initialTimeRange,
  });

  const { effectiveGranularity: granularity } = useGranularity();
  const { data, isFetching, isLoading, refetch, error } = useKpisOverview({
    enabled: initialTimeRangeIsSet && !isPending,
    granularity,
  });
  const {
    data: channelsData,
    isFetching: isChannelsFetching,
    isLoading: isChannelsLoading,
    refetch: refetchChannels,
    error: channelsError,
  } = useKpisPerChannel({
    enabled: initialTimeRangeIsSet && !isPending,
    granularity,
  });

  const { compared } = useDateRangeState();
  const { attributionPreset } = useAttributionPresetState();

  const {
    data: comparedData,
    isFetching: isComparedFetching,
    isLoading: isComparedLoading,
    refetch: refetchCompared,
    error: comparedError,
  } = useKpisOverview({
    enabled: initialTimeRangeIsSet && !isPending && compared,
    granularity,
    useCompare: true,
  });
  const {
    data: channelsComparedData,
    isFetching: isChannelsComparedFetching,
    isLoading: isChannelsComparedLoading,
    refetch: refetchChannelsCompared,
    error: channelsComparedError,
  } = useKpisPerChannel({
    enabled: initialTimeRangeIsSet && !isPending && compared,
    useCompare: true,
    granularity,
  });

  const queriesEnabled = initialTimeRangeIsSet && !isPending;
  const showLoadingState =
    isFetching ||
    isLoading ||
    (compared && (isComparedFetching || isComparedLoading)) ||
    isChannelsFetching ||
    isChannelsLoading ||
    (compared && (isChannelsComparedFetching || isChannelsComparedLoading));
  const hasData = useMemo(() => {
    if (
      showLoadingState ||
      !queriesEnabled ||
      !attributionPreset ||
      isPending
    ) {
      return true;
    }
    return Boolean(
      checkHasOverallData(data?.overall) ||
      checkHasOverallData(comparedData?.overall) ||
      checkHasOverallData(channelsData?.overall) ||
      checkHasOverallData(channelsComparedData?.overall)
    );
  }, [
    attributionPreset,
    showLoadingState,
    isPending,
    queriesEnabled,
    data?.overall,
    comparedData?.overall,
    channelsData?.overall,
    channelsComparedData?.overall,
  ]);

  return (
    <div className="relative">
      <div className="lg:sticky lg:top-0 bg-background pb-2 pt-4 z-navbar">
        <Header
          title="Marketing Overview"
          maxDate={maxDate}
          enableCompare
          loading={showLoadingState || isPending}
          onFetchData={() => {
            refetch();
            refetchChannels();
            if (compared) {
              refetchCompared();
              refetchChannelsCompared();
            }
          }}
        ></Header>
      </div>
      {/* <div className="hidden lg:block">
        <CollapsibleBanner
          id="marketing-overview-dashboard"
          name="Marketing Overview Dashboard"
          videoUrl={EDUCATION_BANNER.MARKETING_OVERVIEW.video}
        />
      </div> */}
      <div className="relative gap-3 lg:gap-4 flex flex-col mt-1 lg:mt-2">
        {hasData || showLoadingState ? null : (
          <div className="bg-background/10 backdrop-blur-sm absolute inset-0 z-40 rounded-lg -m-2 text-center py-16">
            <div className="sticky top-[350px] inset-x-0 text-foreground">
              {axios.isAxiosError(error) && error?.status === 504 ? (
                <div className="max-w-lg mx-auto text-left">
                  {gatewayTimeoutMessage}
                </div>
              ) : (
                <>
                  <p className="font-semibold text-lg">No Data Available</p>
                  <p className="mt-4">
                    There is no data available for the selected timeframe.
                    Please choose a different one.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        <MarketingPerformance
          data={data}
          comparedData={compared ? (comparedData ?? undefined) : undefined}
          channelData={channelsData}
          comparedChannelData={compared ? channelsComparedData : undefined}
          loading={showLoadingState}
          granularity={granularity}
        />
        <MarketingChannelOverviewTable
          error={channelsError}
          data={channelsData}
          comparedData={channelsComparedData}
          loading={
            isChannelsFetching || (compared && isChannelsComparedFetching)
          }
          granularity={granularity}
        />
        <DiscountCodeRedemptions />

        {isDecifyPpsEnabled && <PpsSurveyChannelOverviewChart />}

        <StorePerformanceContainer
          data={data}
          comparedData={compared ? (comparedData ?? undefined) : undefined}
          loading={isFetching || (compared && isComparedFetching)}
          granularity={granularity}
        />
      </div>

      <LearnMoreInfo
        name="Marketing Overview Dashboard"
        url={EDUCATION_BANNER.MARKETING_OVERVIEW.article}
      />
    </div>
  );
}
